<template>
  <base-dialog
    :title="
      dialogAction === 'create' ? $t('common.action.addLocation') : $t('common.action.editLocation')
    "
    :actionName="dialogAction === 'create' ? $t('common.action.create') : $t('common.action.save')"
    @action="dialogAction === 'create' ? addLocation() : updateLocation()"
    :actionDisabled="isValid()"
    :max-width="$vuetify.display.mdAndUp ? '30%' : '100%'"
    :showCancel="$t('common.action.cancel')"
    @close="showDialog = false"
    :isLoading="isLoading"
    :show="showDialog"
  >
    <template #content>
      <div class="mx-4">
        <p class="app-field-label">
          {{ $t("common.action.name") }}
        </p>
        <v-text-field v-model="name" :rules="locationRules" @input="isValid" />
        <p class="app-field-label">
          {{ $t("common.action.address") }}
        </p>
        <v-text-field v-model="countrySelected" disabled />
        <v-text-field
          :label="$t('page.locations.addressLine1')"
          v-model="address1"
          @input="isValid"
          :rules="locationRules"
        />
        <v-text-field :label="$t('page.locations.addressLine2')" v-model="address2" />
        <v-text-field
          :label="$t('page.locations.city')"
          :rules="locationRules"
          @input="isValid"
          v-model="city"
        />
        <v-select
          :items="getStateItem()"
          :label="getStateLabel()"
          :rules="locationRules"
          @input="isValid"
          v-model="state"
        />
        <v-text-field
          :label="getZipLabel()"
          @input="isValid"
          :rules="locationRules"
          v-model="zip"
        />
      </div>
    </template>
  </base-dialog>
</template>

<script lang="ts" setup>
import { ref, computed, watchEffect } from "vue";
import { usaState, supportCountries, canadianProvince } from "@/shared/constants/supportCountries";
import { useLocationStore } from "@/terminals/stores/locations";
import BaseDialog from "@/shared/components/Common/BaseDialog.vue";
import { useValidation } from "@/shared/composables/validation";
import { useAccountStore } from "@/account/stores/account";
import i18n from "@/app/plugins/i18n";
import type { LocationSummary } from "@/terminals/types/locations";

const emit = defineEmits(["updated", "created"]);
const showDialog = ref(false);
const countrySelected = ref<string>();
const usaStates = usaState.map((item) => item.name);
const canadianProvinces = canadianProvince.map((item) => item.name);
const accountCountry = ref<string>();
const address2 = ref("");
const isLoading = ref(false);
const isEdit = computed(() => props.dialogAction === "edit");
const selectedLocation = ref<LocationSummary>();
const { name, address1, city, state, zip, locationRules } = useValidation();

//methods
const getStateName = (stateCode: string) => {
  const stateObj = [...usaState, ...canadianProvince].find(
    (stateItem) => stateItem.code === stateCode,
  );
  return stateObj ? stateObj.name : "";
};

const reset = () => {
  name.value = "";
  address1.value = "";
  address2.value = "";
  city.value = "";
  state.value = "";
  zip.value = "";
};

watchEffect(() => {
  accountCountry.value = useAccountStore().account?.country ?? undefined;

  const { display_name, address } = selectedLocation.value ?? {};
  const countryMapping: Record<string, string> = {
    US: i18n.global.t("common.country.unitedStates"),
    CA: i18n.global.t("common.country.canada"),
  };
  name.value = display_name || "";
  countrySelected.value =
    (accountCountry.value && countryMapping[accountCountry.value]) ||
    i18n.global.t("common.country.unitedStates");
  address1.value = address?.line1 || "";
  address2.value = address?.line2 || "";
  city.value = address?.city || "";
  state.value = getStateName(address?.state ?? "") || "";
  zip.value = address?.postal_code || "";
  if (!(display_name && address && isEdit.value)) {
    reset();
  }
});

const open = async (id?: string) => {
  reset();
  showDialog.value = true;
  isLoading.value = true;
  if (id) {
    const location = await useLocationStore().retrieveLocations(id);
    if (!location) {
      isLoading.value = false;
      return;
    }
    selectedLocation.value = location;
    const { address, display_name } = location;
    const matchingCountry = supportCountries.find((country) => country.code === address?.country);
    name.value = display_name;
    state.value = getStateName(address?.state ?? "");
    countrySelected.value = matchingCountry ? matchingCountry.code : "";
    address1.value = address?.line1 ?? "";
    address2.value = address?.line2 ?? "";
    city.value = address?.city ?? "";
    zip.value = address?.postal_code ?? "";
  }
  isLoading.value = false;
};

defineExpose({
  open,
});

const isValid = () => {
  return !(name.value && address1.value && city.value && state.value && zip.value);
};

const getStateItem = () => {
  return accountCountry.value === "US" ? usaStates : canadianProvinces;
};
const getStateLabel = () => {
  return accountCountry.value === "US"
    ? i18n.global.t("page.locations.state")
    : i18n.global.t("page.locations.province");
};

const getZipLabel = () => {
  return accountCountry.value === "US"
    ? i18n.global.t("page.locations.zip")
    : i18n.global.t("page.locations.postalCode");
};
const addLocation = async () => {
  isLoading.value = true;
  const displayName = name.value;
  const matchingState =
    [...usaState, ...canadianProvince].find((item) => item.name === state.value)?.code ?? "";

  const addressObj = {
    city: city.value,
    country: accountCountry.value ?? "",
    line1: address1.value,
    ...(address2.value.length && { line2: address2.value }),
    postal_code: zip.value,
    state: matchingState,
  };
  const payload = {
    display_name: displayName,
    address: addressObj,
  };

  const location = await useLocationStore().createLocation(payload);

  if (location) {
    emit("created", location);
    await useLocationStore().listLocations(false);
    reset();
    showDialog.value = false;
    emit("updated");
  }

  isLoading.value = false;
};

const updateLocation = async () => {
  if (!selectedLocation.value) {
    return;
  }
  isLoading.value = true;
  const displayName = name.value;
  const matchingState =
    [...usaState, ...canadianProvince].find((item) => item.name === state.value)?.code ?? "";
  const addressObj = {
    city: city.value,
    country: accountCountry.value ?? "",
    line1: address1.value,
    line2: address2.value ?? "",
    postal_code: zip.value,
    state: matchingState,
  };
  const payload = {
    id: selectedLocation.value.id,
    display_name: displayName,
    address: addressObj,
  };
  await useLocationStore().updateLocation(payload);
  await useLocationStore().listLocations(false);
  isLoading.value = false;
  showDialog.value = false;
  emit("updated");
};

const props = defineProps({
  dialogAction: {
    type: String,
    default: "create",
  },
});
</script>
